import { FC, useState } from 'react'
import { Typography, IconButton, Box, CircularProgress } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import axios from 'lib/axios'

import { Dialog } from './nexaAIToggleModal.styles'
import ThemeButton from 'ui/components/buttons/primary/ThemeButton'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { closeModal, modalPropsSelector } from 'store/modals/modalSlice'
import { openToast, closeToast, ToastVariant, ToastModel } from 'store/toasts/toastSlice'
import { setNexaAIAgentState } from 'store/user/userSlice'
import { AgentStateType } from 'models'

const NexaAIToggleModal: FC = () => {
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const [isLoading, setIsLoading] = useState(false)

  const isEnabled = modalProps?.props?.nexaAIEnabled ?? false
  const agentName = modalProps?.props?.nexaAIAgentName

  const handleClose = () => {
    dispatch(closeModal())
  }

  const handleConfirm = async () => {
    setIsLoading(true)

    try {
      if (isEnabled && agentName) {
        // Disable - Remove agent
        await axios.delete(`/api/v4/agents/${agentName}`)

        dispatch(setNexaAIAgentState(AgentStateType.REMOVING))

        dispatch(
          openToast({
            variant: ToastVariant.success,
            toastModel: ToastModel.Trends,
            props: {
              text: 'Nexa AI agent is being removed. This may take a moment.',
              description: ''
            }
          })
        )
      } else {
        // Enable - Deploy agent
        await axios.post('/api/v4/agents/deploy')

        dispatch(setNexaAIAgentState(AgentStateType.PENDING))

        dispatch(
          openToast({
            variant: ToastVariant.success,
            toastModel: ToastModel.NexaAI,
            props: {
              text: 'Nexa AI is being activated. This may take up to a few minutes.',
              description: ''
            }
          })
        )
      }

      handleClose()

      if (isEnabled) {
        setTimeout(() => {
          dispatch(closeToast())
        }, 5000)
      }
    } catch (error: any) {
      console.error('Nexa AI Agent toggle error:', error)

      const errorMessage = error?.response?.data?.message || 'An error occurred'

      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: isEnabled ? 'Failed to Disable Nexa AI Agent' : 'Failed to Enable Nexa AI Agent',
            description: errorMessage
          }
        })
      )

      setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog onClose={handleClose} aria-labelledby="nexa-ai-toggle-dialog" open={modalProps.open}>
      <Box className="header">
        <Typography className="header-title">
          {isEnabled ? 'Disable Nexa AI Agent?' : 'Enable Nexa AI Agent?'}
        </Typography>
        <IconButton className="close-button" aria-label="close" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className="content">
        {isEnabled ? (
          <Typography className="description">
            Disabling the Nexa AI agent will permanently remove all conversation history and previously generated
            responses. This action cannot be undone. The AI agent will no longer have access to your organization&apos;s
            security data until re-enabled.
          </Typography>
        ) : (
          <Typography className="description">
            The Nexa AI agent will have access to your organization&apos;s security data to provide responses and
            perform data analysis. All data will remain strictly within your organization&apos;s environment and will
            never be shared externally. As a best practice, we recommend validating AI-generated responses prior to
            taking action.
          </Typography>
        )}
      </Box>

      <Box className="footer">
        <ThemeButton
          themevariant="secondary"
          shape="rectangular"
          onClick={handleClose}
          disabled={isLoading}
          sx={{ flex: 1 }}
        >
          Cancel
        </ThemeButton>
        <ThemeButton
          themevariant="primary"
          shape="rectangular"
          onClick={handleConfirm}
          disabled={isLoading}
          sx={{ flex: 1 }}
        >
          {isLoading ? <CircularProgress size={20} color="inherit" /> : isEnabled ? 'Disable' : 'Enable'}
        </ThemeButton>
      </Box>
    </Dialog>
  )
}

export default NexaAIToggleModal
